import { Box, Button, Card, CardActions, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import React, { useContext, useEffect, useState } from 'react';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { useManualPropertiesContext } from './context';
import Loading from 'views/App/Portal/Loading';
import useManualTypes from 'data_sources/useManualTypes';
import { ManualType } from 'types/manualType';
import { Form } from 'components/_forms';
import useManageManual from '../_data_sources/useManageManual';
import { isEmpty } from 'lodash';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import { ServerErrorResponse } from 'data_sources/constants';
import errorHelper from 'helpers/errorHelper';
import useVerticals from 'data_sources/useVerticals';
import { Vertical } from 'types/vertical';
import PolicyHeadersConfiguration from '../../shared/PolicyHeadersConfiguration';
import {
  HeaderConfigurationRequest,
  PolicyHeaderDateFillOption,
} from '../../_types/manuals/draft';

const GO_BACK_URL = '/admin/policy/draft-manuals';

// Helper function to convert frontend option to backend boolean
const mapFrontendToBackend = (option: PolicyHeaderDateFillOption): boolean => {
  return option === 'AUTO_FILL_ONLY';
};

export const ManualProperties = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isFeatureEnabled } = useContext(AppContext);
  const {
    isLoading: isLoadingContext,
    isError,
    draftManual,
    headerState,
    dateFormat,
    effectiveDateOption,
    revisedDateOption,
    issuingAuthorityType,
    headerOrder,
    customFields,
    agencyLogoId,
    issuingAuthoritySignatureId,
    issuingAuthorityName,
  } = useManualPropertiesContext();

  const manualTypesQuery = useManualTypes();
  const verticalsQuery = useVerticals();
  const { updateProperties, removeLock: removeLockManual, takeLock } = useManageManual();

  const [title, setTitle] = useState('');
  const [manualType, setManualType] = useState<ManualType>();
  const [vertical, setVertical] = useState<Vertical>();
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!manualType && Array.isArray(manualTypesQuery.data) && manualTypesQuery.data.length > 0) {
      const foundType = manualTypesQuery.data.find((type) => type.id === draftManual.manualTypeId);
      if (foundType) {
        setManualType(foundType);
      }
    }
  }, [manualTypesQuery.data, manualTypesQuery.isLoading, draftManual.manualTypeId, manualType]);

  useEffect(() => {
    if (!vertical && Array.isArray(verticalsQuery.data) && verticalsQuery.data.length > 0) {
      const foundVertical = verticalsQuery.data.find((type) => type.id === draftManual.verticalId);
      if (foundVertical) {
        setVertical(foundVertical);
      }
    }
  }, [verticalsQuery.data, verticalsQuery.isLoading, draftManual.verticalId, vertical]);

  useEffect(() => {
    if (draftManual.id) {
      setTitle(draftManual.name);
      setDescription(draftManual.description);

      takeLock(
        draftManual.id ?? '',
        () => { },
        (error: ServerErrorResponse) => {
          errorHelper.enqueueErrors(error);
        }
      );
    }
  }, [draftManual.id, draftManual.name, draftManual.description]);

  if (isLoadingContext) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Typography
        variant='body1'
        color='error'
      >
        {t('draftManuals.manualProperties.pageLoadError')}
      </Typography>
    );
  }

  const handleCancel = () => {
    navigate(GO_BACK_URL);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!title || !draftManual.manualTypeId) {
      setIsLoading(false);
      return;
    }

    const updateData: {
      name: string;
      manualTypeId: string;
      description: string;
      headerConfiguration?: HeaderConfigurationRequest;
    } = {
      name: title,
      manualTypeId: manualType?.id || draftManual.manualTypeId || '',
      description: description,
    };

    // Only include policy header settings for AGENCY level manuals
    if (draftManual.manualLevel?.toLowerCase() === 'agency') {
      if (headerState === 'ENABLED') {
        updateData.headerConfiguration = {
          state: headerState,
          effectiveDateFormat: dateFormat,
          revisedDateFormat: dateFormat,
          autoSetEffectiveDate: mapFrontendToBackend(effectiveDateOption),
          autoSetRevisedDate: mapFrontendToBackend(revisedDateOption),
          agencyLogo: agencyLogoId || undefined,
          issuingAuthorityType: issuingAuthorityType,
          issuingAuthoritySignature: issuingAuthoritySignatureId || undefined,
          issuingAuthorityName: issuingAuthorityName || undefined,
          customFields: customFields,
          order: headerOrder,
        };
      } else {
        updateData.headerConfiguration = {
          state: 'DISABLED',
        };
      }
    }

    await updateProperties(draftManual.id ?? '', updateData);

    removeLockManual(
      draftManual.id ?? '',
      () => {
        queryClient.invalidateQueries(['draft_manual', draftManual.id ?? '']);
        enqueueSnackbar('Structure saved succesfully', {
          variant: 'success',
          autoHideDuration: 4000,
        });
        setIsLoading(false);
        navigate(-1);
      },
      (error: ServerErrorResponse) => {
        errorHelper.enqueueErrors(error);
        setIsLoading(false);
      }
    );
  };

  const nonAgencyReadOnlyView = (
    <Card
      sx={{ maxWidth: 666, mx: 'auto', width: '100%' }}
      className='lex-form'
    >
      <CardContent>
        <Stack
          spacing={4}
          gap={6}
        >
          <Box>
            <Typography variant='subtitleSm'>
              {t('draftManuals.manualProperties.readOnlyValues.jurisdiction')}
            </Typography>
            <Typography variant='bodySm'>{draftManual.jurisdiction || 'Global'}</Typography>
          </Box>

          <Box>
            <Typography variant='subtitleSm'>{t('draftManuals.manualProperties.readOnlyValues.vertical')}</Typography>
            <Typography variant='bodySm'>{vertical?.name}</Typography>
          </Box>

          <Box>
            <Typography variant='subtitleSm'>{t('draftManuals.manualProperties.readOnlyValues.manualType')}</Typography>
            <Typography variant='bodySm'>{manualType?.name ?? ''}</Typography>
          </Box>

          <Box>
            <Typography variant='subtitleSm'>{t('draftManuals.manualProperties.readOnlyValues.title')}</Typography>
            <Typography variant='bodySm'>{draftManual.name}</Typography>
          </Box>
        </Stack>
      </CardContent>
      <CardActions>
        <Box />
        <Button
          component='button'
          type='submit'
          variant='contained'
          onClick={() => navigate(GO_BACK_URL)}
        >
          {t('draftManuals.manualProperties.formControls.returnToManuals')}
        </Button>
      </CardActions>
    </Card>
  );

  const agencyEditableForm = (
    <Card
      sx={{ maxWidth: 666, mx: 'auto', width: '100%' }}
      className='lex-form'
    >
      <Form onSubmit={handleSubmit}>
        <CardContent>
          <Stack
            spacing={4}
            gap={6}
          >
            {/* Read-only fields */}
            <Box>
              <Typography variant='subtitleSm'>
                {t('draftManuals.manualProperties.readOnlyValues.jurisdiction')}
              </Typography>
              <Typography variant='bodySm'>{draftManual.jurisdiction || 'Global'}</Typography>
            </Box>

            <Box>
              <Typography variant='subtitleSm'>{t('draftManuals.manualProperties.readOnlyValues.vertical')}</Typography>
              <Typography variant='bodySm'>{vertical?.name}</Typography>
            </Box>

            <Box>
              <Typography variant='subtitleSm'>
                {t('draftManuals.manualProperties.readOnlyValues.manualType')}
              </Typography>
              <Typography variant='bodySm'>{manualType?.name ?? ''}</Typography>
            </Box>

            {/* Editable fields */}
            <TextField
              id='title'
              label={t('draftManuals.manualProperties.readOnlyValues.title')}
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />

            <TextField
              id='description'
              label={t('draftManuals.manualProperties.readOnlyValues.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              type='textarea'
              fullWidth
            />

            {/* Policy Headers Section - Only for AGENCY manuals */}
            {isFeatureEnabled('policy-headers') && <PolicyHeadersConfiguration />}
          </Stack>
        </CardContent>
        <CardActions>
          <Box />
          <Stack
            direction='row'
            spacing={2}
          >
            <Button onClick={handleCancel}>{t('draftManuals.manualProperties.formControls.cancel')}</Button>
            <Button
              disabled={isLoading || isEmpty(title) || !draftManual.manualTypeId}
              component='button'
              type='submit'
              variant='contained'
              loading={isLoading}
              loadingPosition='end'
            >
              {t('draftManuals.manualProperties.formControls.save')}
            </Button>
          </Stack>
        </CardActions>
      </Form>
    </Card>
  );

  return (
    <TakeoverLayout
      backUrl={GO_BACK_URL}
      pageTitle={t('draftManuals.manualProperties.pageTitle')}
    >
      <Box>{draftManual.manualLevel?.toLowerCase() === 'agency' ? agencyEditableForm : nonAgencyReadOnlyView}</Box>
    </TakeoverLayout>
  );
};
